import { Company, Pivot } from 'api';
import APP_MESSAGE from 'constant/message';
import APP_ROUTE from 'constant/route';
import {
  setCompanyId,
  setStaffId,
  setUserId,
  setUserToken,
  setMobile,
  setAuthUserInfo,
  getLoginRedirectUrl,
  clearLoginRedirectUrl,
  setOrgCode
} from 'data/storage';
import { AppEpic } from 'model';
import { appMessageError, appMessageLoading, appMessageSuccess, appRedirectAfterLogout, resetAppData } from 'model/app';
import { combineEpics } from 'redux-observable';
import { forkJoin, of, from } from 'rxjs';
import { filter, mergeMap, map, catchError, startWith, delay, withLatestFrom, tap } from 'rxjs/operators';
import { push } from 'connected-react-router';

import {
  getCode,
  loginByMobileFulfilled,
  loginByMobile,
  loginByWorkChat,
  loginByMobileRejected,
  getCsrfToken,
  getUserInfoByToken,
  getWeChatToken,
  getWxLoginConfig,
  setCsrfToken,
  loginByMobilePending,
  loginOut,
  resetLoginStatus,
  setPasswordLoginData,
  checkUserRegistered,
  loginByPassword,
  setGraphicCaptchaUrl,
  registerByPassword,
  loginByResetPassword,
  refrashGraphicCaptchaUrl,
  afterLogin,
  updateUserInfo
} from '.';
import { isSSO } from 'util/crossenv';
import { getSessionId } from 'util/session';
import Grayscale from 'lib/grayscalePublishing';

type GetCodeAction = ReturnType<typeof getCode>;
type LoginByMobileAction = ReturnType<typeof loginByMobile>;
type ThirdUserInfo = ReturnType<typeof updateUserInfo>;

// 获取手机验证码
const getMobileCaptchasEpic: AppEpic = (action$, state$) =>
  action$.pipe(
    filter(getCode.match),
    withLatestFrom(state$),
    mergeMap(([action]) => {
      return Pivot.getMobileCaptchas(action.payload, 5).pipe(
        mergeMap(() => {
          return of(appMessageSuccess(APP_MESSAGE.GET_CAPTCHAS_FULFILLED));
        }),
        catchError(() => of(appMessageError(APP_MESSAGE.GET_CAPTCHAS_REJECTED)))
      );
    })
  );

// 手机验证码登录
const loginByMobileEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(loginByMobile.match),
    mergeMap(({ payload: { mobileCaptcha, mobile, sessionId } }) =>
      Pivot.loginByMobile(mobileCaptcha, mobile, sessionId).pipe(
        mergeMap(({ userId, access_token: accessToken }) => {
          setAuthUserInfo({
            userId,
            token: accessToken,
            mobile
          });
          return _getCompanyListAndRedirect();
        }),
        startWith(loginByMobilePending()),
        catchError(() => {
          return of(loginByMobileRejected());
        })
      )
    )
  );

// 企业微信登录
const loginByWorkChatEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(loginByWorkChat.match),
    mergeMap(() => {
      return _getCompanyListAndRedirect();
    }),
    catchError(() => {
      return of(loginByMobileRejected());
    })
  );

// 获取企业列表
const getCompanyList = (redirectUrl?: string) =>
  forkJoin([Company.getCompanyList(), Pivot.getStaff()]).pipe(
    mergeMap(([companyList, personConfig]) => {
      /**
       * pc登录状态
       * 1. 无企业，手动登录
       * 2. 只有一个企业或者已选择企业
       * 3. 多企业且未选择过企业
       */
      if (!companyList.length) {
        return of({
          redirectUrl: APP_ROUTE.NONE,
          companyList
        });
      }

      const onlyOneCompany = companyList.length === 1;
      if (personConfig.data || onlyOneCompany) {
        const staffId = personConfig.data?.id || companyList[0].staffId;
        setStaffId(staffId);

        if (onlyOneCompany) {
          setCompanyId(companyList[0].companyId);
          setOrgCode(companyList[0].orgCode);
        } else {
          const company = companyList.find((c) => c.staffId === staffId);
          if (company && company.companyId) setCompanyId(company.companyId);
          if (company && company.orgCode) setOrgCode(company.orgCode);
        }

        if (!personConfig.data || !personConfig.data.id) {
          return Pivot.setStaff(staffId).pipe(
            map(() => ({
              redirectUrl: redirectUrl || APP_ROUTE.SCHEDULE,
              companyList
            }))
          );
        }

        return of({
          redirectUrl: redirectUrl || APP_ROUTE.SCHEDULE,
          companyList
        });
      }

      return of({
        redirectUrl: APP_ROUTE.SWICH,
        companyList
      });
    })
  );

const _getCompanyListAndRedirect = (path?: string) => {
  const redirectUrl = path || getLoginRedirectUrl() || '';

  clearLoginRedirectUrl();

  return from(getCompanyList(redirectUrl)).pipe(
    mergeMap(({ redirectUrl }) =>
      // 使用 href 替代 push(redirectUrl)，进行灰发资源匹配
      of(resetLoginStatus()).pipe(delay(2000), Grayscale.support ? tap(() => (location.href = redirectUrl)) : startWith(push(redirectUrl)))
    ),
    startWith(loginByMobileFulfilled())
  );
};

// 获取微信扫码登录配置
const getWxLoginConfigEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(getWxLoginConfig.match),
    mergeMap(() =>
      Pivot.getWxLoginConfig().pipe(
        map((config) => {
          return config;
        })
      )
    )
  );

// 微信扫码登录，账号系统重定向后，通过用户token获取用户信息
const getUserInfoByTokenEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(getUserInfoByToken.match),
    mergeMap((action) =>
      Pivot.getUserInfoByToken(action.payload).pipe(
        mergeMap((data) => {
          setUserId(data.userId);
          setMobile(data.mobile);
          return _getCompanyListAndRedirect();
        }),
        startWith(loginByMobilePending()),
        catchError(() => {
          return of(loginByMobileRejected());
        })
      )
    )
  );

// 嵌入第三方的网页获取用户信息
const updateUserInfoEpic: AppEpic = (action$) => {
  return action$.pipe(
    filter(updateUserInfo.match),
    mergeMap((action: ThirdUserInfo) => {
      setUserToken(action.payload.accessToken);
      setUserId(action.payload.userId);
      return _getCompanyListAndRedirect(action.payload.path);
    })
  );
};

// 微信扫码登录，获取扫码token，账号系统会根据这个token进行重定向
const getCsrfTokenEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(getCsrfToken.match),
    mergeMap(() =>
      Pivot.getLoginCsrfToken().pipe(
        mergeMap(({ csrfToken }) => {
          if (!csrfToken) {
            return of(loginByMobileRejected());
          }
          return of(setCsrfToken(csrfToken), resetLoginStatus());
        }),
        startWith(loginByMobilePending()),
        catchError(() => {
          return of(loginByMobileRejected());
        })
      )
    )
  );

// 微信扫码登录，账号系统重定向后，获取用户token
const getWeChatTokenEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(getWeChatToken.match),
    mergeMap(() =>
      Pivot.getWeChatToken().pipe(
        mergeMap((accessToken) => {
          setUserToken(accessToken);
          return of(getUserInfoByToken(accessToken));
        })
      )
    )
  );

// logout后重定向到统一认证
const loginOutEpic: AppEpic = (action$) => {
  return action$.pipe(
    filter(loginOut.match),
    mergeMap(() => {
      const _isSSO = isSSO();
      const logoutFn = _isSSO ? Pivot.ssoLogout : Pivot.loginOut;
      return logoutFn().pipe(
        mergeMap(() => {
          appMessageSuccess(APP_MESSAGE.LOGIN_OUT_FULFILLED);
          if (_isSSO) {
            return of(appRedirectAfterLogout());
          } else {
            return of(resetAppData(), push(APP_ROUTE.LOGIN));
          }
        }),
        catchError((error) => {
          if (error.response.status !== 401) return of(appMessageError(APP_MESSAGE.LOGIN_OUT_REJECTED), resetAppData());
          return of(appMessageSuccess(APP_MESSAGE.LOGIN_OUT_FULFILLED));
        }),

        startWith(appMessageLoading(APP_MESSAGE.LOGIN_OUT_PENDING))
      );
    })
  );
};

// 检查用户是否注册（私有化）
const checkUserRegisteredEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(checkUserRegistered.match),
    mergeMap((action) =>
      Pivot.checkUserRegistered(action.payload).pipe(
        mergeMap(() => {
          return of(setPasswordLoginData({ isUserRegistered: true }));
        }),
        catchError(() => {
          return of(setPasswordLoginData({ isUserRegistered: false }));
        })
      )
    )
  );

// 密码登录（公版、私有化）
const loginByPasswordEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(loginByPassword.match),
    mergeMap((action) =>
      Pivot.loginByPassword(action.payload).pipe(
        mergeMap(({ userId, access_token: accessToken }) => {
          setAuthUserInfo({
            userId,
            token: accessToken,
            mobile: action.payload.username
          });
          return _getCompanyListAndRedirect();
        }),
        startWith(loginByMobilePending()),
        catchError((error) => {
          const errorCode = error.response.data.code;
          const newData = {} as any;
          if (errorCode === 4001003) {
            return of(refrashGraphicCaptchaUrl({ force: true }), loginByMobileRejected());
          } else if (errorCode === 4001004) {
            newData.graphicCaptchaError = '图形验证码错误';
            return of(refrashGraphicCaptchaUrl({ force: true }), setPasswordLoginData(newData), loginByMobileRejected());
          } else if (errorCode === 4001000 || errorCode === 4001007) {
            newData.passwordError = '账号不存在或密码错误，请重新输入';
            // TODO  判断是否需要图形验证码
            return of(refrashGraphicCaptchaUrl({ force: false }), setPasswordLoginData(newData), loginByMobileRejected());
          }
          return of(setPasswordLoginData(newData), loginByMobileRejected());
        })
      )
    )
  );

// 密码注册（私有化）
const registerByPasswordEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(registerByPassword.match),
    mergeMap((action) =>
      Pivot.registerByPassword(action.payload).pipe(
        mergeMap(({ userId, access_token: accessToken }) => {
          setAuthUserInfo({
            userId,
            token: accessToken,
            mobile: action.payload.username
          });
          return _getCompanyListAndRedirect();
        }),
        startWith(loginByMobilePending()),
        catchError(() => {
          return of(loginByMobileRejected());
        })
      )
    )
  );

// 重置密码后登录系统
const loginByResetPasswordEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(loginByResetPassword.match),
    mergeMap((action) => {
      setAuthUserInfo(action.payload);
      return _getCompanyListAndRedirect();
    })
  );

const refrashGraphicCaptchaUrlEpic: AppEpic = (action$, state$) =>
  action$.pipe(
    filter(refrashGraphicCaptchaUrl.match),
    withLatestFrom(state$),
    mergeMap(([action, state]) => {
      return from(getSessionId()).pipe(
        mergeMap((sessionId) => {
          if (action.payload.force === false && !state.login.passwordLogin.graphicCaptchaUrl) return of(setGraphicCaptchaUrl(''));
          const url = `/api-auth/api/graphicCaptchas?sessionId=${sessionId}&timestamp=${new Date().getTime()}`;
          return of(setGraphicCaptchaUrl(url));
        })
      );
    })
  );

// 登录成功跳转
const afterLoginEpic: AppEpic = (action$) =>
  action$.pipe(
    filter(afterLogin.match),
    mergeMap((action) => {
      setAuthUserInfo(action.payload);
      return _getCompanyListAndRedirect();
    })
  );

export type LoginEpicActions = GetCodeAction | LoginByMobileAction;

export default combineEpics(
  getMobileCaptchasEpic,
  loginByMobileEpic,
  getCsrfTokenEpic,
  getWeChatTokenEpic,
  getWxLoginConfigEpic,
  getUserInfoByTokenEpic,
  loginOutEpic,
  loginByWorkChatEpic,
  checkUserRegisteredEpic,
  loginByPasswordEpic,
  registerByPasswordEpic,
  loginByResetPasswordEpic,
  refrashGraphicCaptchaUrlEpic,
  afterLoginEpic,
  updateUserInfoEpic
);
